// src/pages/MainPage/MainPage.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import block from "bem-cn";
import { addDays, format } from "date-fns";
import { observer } from "mobx-react";

import { charactersStore, userStore } from "stores";

import { Loader, PanelWrapper } from "components";
import { RouterPathEnum } from "shared/enums/commonEnums";

import CharactersList from "./components/CharactersList";
// import Footer from "./components/Footer";
// import { MainPageHeader } from "./components";

import "./MainPage.scss";

const cnMainPage = block("MainPage");
const DAYS_RANGE = 7;

export enum Role {
  Doctor = "Doctor",
  Patient = "Patient",
  Manager = "Manager",
  User = "user",
}

const DAY_NAMES_RU = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const MONTH_NAMES_RU = [
  "Янв",
  "Фев",
  "Мар",
  "Апр",
  "Май",
  "Июн",
  "Июл",
  "Авг",
  "Сен",
  "Окт",
  "Ноя",
  "Дек",
];

const MainPage = () => {
  const navigate = useNavigate();
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );

  // Форматирование даты на русский
  const formatRu = (date: Date) => {
    const dayName = DAY_NAMES_RU[date.getDay()];
    const dayNumber = date.getDate();
    const monthName = MONTH_NAMES_RU[date.getMonth()];
    return { dayName, dayNumber, monthName };
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate(RouterPathEnum.Auth);
      return;
    }

    const fetchData = async () => {
      try {
        await userStore.getProfile();
        await charactersStore.getCharacters();
      } catch (error) {
        console.error("Ошибка загрузки данных:", error);
      }
    };
    fetchData();

    const statusCheck = setTimeout(() => {
      if (userStore.user?.role === Role.Manager) {
        navigate(RouterPathEnum.DoctorPage);
      }
    }, 500);

    return () => clearTimeout(statusCheck);
  }, [navigate]);

  // Загрузка уровней и квестов для выбранной даты
  const loadDataForDate = async (date: string) => {
    if (!charactersStore.selectedCharacter) return;
    setLoadingLevels(true);

    console.log(
      `🔹 Загрузка уровней и завершённых квестов для персонажа ${charactersStore.selectedCharacter.name} на дату ${date}`
    );

    await Promise.all([
      charactersStore.loadLevelsForDate(date),
      charactersStore.loadCompletedQuests(date),
    ]);

    console.log(
      `✅ Данные для ${charactersStore.selectedCharacter.name} на ${date} загружены. Уровней: ${charactersStore.availableLevels.length}, Квестов: ${charactersStore.completedQuests.length}`
    );
    setLoadingLevels(false);
  };

  // При смене персонажа выбираем сегодняшнюю дату и подгружаем данные
  useEffect(() => {
    if (!charactersStore.selectedCharacter) return;
    const today = format(new Date(), "yyyy-MM-dd");
    setSelectedDate(today);
    loadDataForDate(today);
  }, [charactersStore.selectedCharacter]);

  const handleDateSelect = async (date: string) => {
    setSelectedDate(date);
    await loadDataForDate(date);
  };

  // Датапикер
  const renderDatePicker = () => {
    const buttons = [];
    for (let i = -DAYS_RANGE; i <= DAYS_RANGE; i++) {
      const dateObj = addDays(new Date(selectedDate), i);
      const dateStr = format(dateObj, "yyyy-MM-dd");

      const { dayName, dayNumber, monthName } = formatRu(dateObj);
      const isSelected = dateStr === selectedDate;
      const levelCount = isSelected
        ? charactersStore.availableLevels.length
        : 0;

      let bgColor = "#00c6ff";
      if (isSelected) {
        const t = Math.min(levelCount / 8, 1);
        const red = Math.round(255 * (1 - t));
        const green = 0;
        const blue = Math.round(255 * t);
        bgColor = `rgb(${red}, ${green}, ${blue})`;
      }

      buttons.push(
        <Tooltip key={dateStr} title={`${levelCount} уровней`} arrow>
          <Box
            onClick={() => handleDateSelect(dateStr)}
            sx={{
              cursor: "pointer",
              width: 70,
              height: 90,
              mr: 1,
              borderRadius: 1,
              border: isSelected ? "2px solid #0072ff" : "1px solid #ccc",
              backgroundColor: bgColor,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              "&:hover": { transform: "scale(1.05)" },
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: "bold", textAlign: "center" }}
            >
              {dayName}
            </Typography>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              {dayNumber}
            </Typography>
            <Typography variant="caption" sx={{ textAlign: "center" }}>
              {monthName}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Box display="flex" justifyContent="center" overflow="auto" mb={2} px={1}>
        {buttons}
      </Box>
    );
  };

  const clientRole = userStore.user?.role;

  const renderContent = () => {
    switch (clientRole) {
      case Role.User:
        return (
          <Box
            className={cnMainPage("pageContent").toString()}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 64px)",
              padding: 2,
              justifyContent: "space-between",
            }}
          >
            {/* Персонажи */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Выберите персонажа
              </Typography>

              {charactersStore.characters.length < 5 && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    mb: 2,
                    fontWeight: "bold",
                    fontSize: 14,
                    borderRadius: 2,
                    textTransform: "none",
                    padding: "8px 8px",
                    background: "linear-gradient(90deg, #00c6ff, #0072ff)",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #0072ff, #00c6ff)",
                    },
                  }}
                  onClick={() => navigate(RouterPathEnum.CreateCharacterPage)}
                >
                  Создать нового персонажа
                </Button>
              )}

              <CharactersList
                onCharacterSelect={async (characterId: number) => {
                  charactersStore.selectCharacter(characterId);
                }}
              />

              {charactersStore.selectedCharacter && renderDatePicker()}
            </Box>

            {/* Уровни и квесты в одном ряду */}
            {/* Уровни и завершённые квесты в одном ряду */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Уровни и завершённые квесты
              </Typography>
              {charactersStore.selectedCharacter ? (
                loadingLevels ? (
                  <Loader visible />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    {[
                      // приводим к одному формату, чтобы отсортировать
                      ...charactersStore.availableLevels.map((lvl, idx) => ({
                        id: lvl.recordId || `${idx}-${Date.now()}`,
                        title: lvl.name,
                        time: lvl.createdAt,
                        type: "level",
                        data: lvl,
                      })),
                      ...charactersStore.completedQuests.map((quest, idx) => ({
                        id: quest.ID || idx,
                        title: quest.TaskTitle,
                        time: quest.EndTime,
                        type: "quest",
                        data: quest,
                      })),
                    ]
                      // сортируем по времени (раньше → позже)
                      .sort(
                        (a, b) =>
                          new Date(a.time).getTime() -
                          new Date(b.time).getTime()
                      )
                      .map((item) =>
                        item.type === "level" ? (
                          <Box
                            key={item.id}
                            onClick={() => {
                              const lvl = item.data;
                              navigate(RouterPathEnum.SceneMuscle, {
                                state: {
                                  recordId: lvl.recordId,
                                  level: lvl.level,
                                  createdAt: lvl.createdAt,
                                },
                              });
                            }}
                            sx={{
                              cursor: "pointer",
                              width: 120,
                              height: 120,
                              m: 1,
                              borderRadius: 2,
                              border: "1px solid #0072ff",
                              background:
                                "linear-gradient(90deg, #00c6ff, #0072ff)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              color: "#fff",
                              fontWeight: "bold",
                              "&:hover": { transform: "scale(1.05)" },
                              transition: "all 0.2s",
                            }}
                          >
                            <Typography variant="body2">
                              {item.title}
                            </Typography>
                            <Typography variant="caption">
                              {new Date(item.time).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            key={item.id}
                            onClick={() => {
                              const quest = item.data;
                              navigate("/profile/scenemuscle", {
                                state: {
                                  questId: quest.ID,
                                  title: quest.TaskTitle,
                                  type: quest.TaskType,
                                  completedAt: quest.EndTime,
                                },
                              });
                            }}
                            sx={{
                              cursor: "pointer",
                              width: 120,
                              height: 120,
                              m: 1,
                              borderRadius: 2,
                              border: "1px solid #28a745",
                              background:
                                "linear-gradient(90deg, #85e085, #28a745)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              color: "#fff",
                              fontWeight: "bold",
                              "&:hover": { transform: "scale(1.05)" },
                              transition: "all 0.2s",
                            }}
                          >
                            <Typography variant="body2">
                              {item.title}
                            </Typography>
                            <Typography variant="caption">
                              {new Date(item.time).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </Box>
                        )
                      )}
                  </Box>
                )
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Сначала выберите персонажа
                </Typography>
              )}
            </Box>
          </Box>
        );

      case Role.Doctor:
        return (
          <PanelWrapper>
            <div>Здесь будет интерфейс для доктора (заглушка)</div>
          </PanelWrapper>
        );

      case Role.Patient:
        return (
          <PanelWrapper>
            <div>Привет! Я пациент!</div>
          </PanelWrapper>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cnMainPage()}>
      {/* <Box className={cnMainPage("header").toString()}>
        <MainPageHeader />
      </Box> */}

      {renderContent()}

      {/* <Footer /> */}
    </div>
  );
};

export default observer(MainPage);
